import React, { useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEgg,
  faSkull,
  faDove,
  faUtensils,
  faAngleDoubleLeft, // Íconos de flechitas
} from "@fortawesome/free-solid-svg-icons";
import AddFarm from "../Add/Buttons/AddFarm/AddFarm";
import AddGalpon from "../Add/Buttons/AddGalpon/AddGalpon";
import AddBatch from "../Add/Buttons/AddBatch/AddBatch";
import ReduceAves from "../Add/Buttons/ReduceAves/ReduceAves";
import { useShoppingContext } from "../../context/ShoppingContext";
import { format, isToday } from "date-fns";
import "./DashboardButtons.css"; // Importa el archivo CSS
import { useNavigate } from "react-router-dom";
import AddAlimento from "../Add/Buttons/AddAlimento/AddAlimento";

const DashboardButtons = ({ speed = 30 }) => {
  const {
    aves,
    produccionHuevos,
    controlAves,
    controlAlimentos,
    fetchControlAves,
    fetchMisAves,
    fetchMisGalpones,
    fetchProduccionHuevos,
    fetchControlAlimentos,
    fetchMisGranjas,
    isConfiguracionPage,
    isAlimentosPage,
    isAvesPage,
    isGalponesPage,
    isGranjaPage,
    isDarkMode,
  } = useShoppingContext();

  const navigate = useNavigate();
  const tickerRef = useRef(null); // Ref para el contenedor del ticker

  useEffect(() => {
    fetchMisGranjas();
    fetchMisGalpones();
    fetchMisAves();
    fetchProduccionHuevos();
    fetchControlAves();
    fetchControlAlimentos();
  }, [
    fetchMisGranjas,
    fetchMisGalpones,
    fetchMisAves,
    fetchProduccionHuevos,
    fetchControlAves,
    fetchControlAlimentos,
  ]);

  const totalAves = aves.reduce((sum, ave) => sum + ave.saldo, 0);
  const produccionTotalHuevos = produccionHuevos.reduce(
    (sum, prod) => sum + prod.comerciales,
    0
  );
  const produccionPercentage =
    totalAves > 0 ? ((produccionTotalHuevos / totalAves) * 100).toFixed(2) : 0;

  const lastControlAves =
    controlAves.length > 0
      ? controlAves.reduce((latest, current) => {
          const latestDate = new Date(latest.fecha);
          const currentDate = new Date(current.fecha);
          return currentDate > latestDate ? current : latest;
        })
      : null;

  const lastMortalidad = lastControlAves
    ? controlAves.filter(
        (control) =>
          format(new Date(control.fecha), "yyyy-MM-dd") ===
          format(new Date(lastControlAves.fecha), "yyyy-MM-dd")
      ).length
    : 0;
  const lastMortalidadDate = lastControlAves
    ? new Date(lastControlAves.fecha)
    : new Date();

    if (lastMortalidadDate) {
      lastMortalidadDate.setDate(lastMortalidadDate.getDate() + 1); // Sumar 1 día
    }

  const formattedMortalidadDate = isToday(lastMortalidadDate)
    ? "hoy"
    : format(lastMortalidadDate, "dd/MM/yyyy");

  const totalAlimentoConsumido = controlAlimentos.reduce(
    (sum, control) => sum + control.cantidad,
    0
  );

  const lastControlAlimento =
    controlAlimentos.length > 0
      ? controlAlimentos.reduce((latest, current) => {
          const latestDate = new Date(latest.fecha);
          const currentDate = new Date(current.fecha);
          return currentDate > latestDate ? current : latest;
        })
      : null;

  const lastAlimentoDate = lastControlAlimento
    ? new Date(lastControlAlimento.fecha)
    : new Date();

  if (lastControlAlimento) {
    lastAlimentoDate.setDate(lastAlimentoDate.getDate() + 1); // Sumar 1 día
  }
  const formattedAlimentoDate = isToday(lastAlimentoDate)
    ? "hoy"
    : format(lastAlimentoDate, "dd/MM/yyyy");

  const tickerItems = [
    {
      icon: faDove,
      color: "rgb(222 208 17)",
      text: `${totalAves} aves`,
    },
    {
      icon: faEgg,
      color: "rgb(255 176 125)",
      text: `${produccionPercentage}% producción`,
    },
    {
      icon: faSkull,
      color: "rgb(255 101 101)",
      text: `${lastMortalidad} mortalidad (${formattedMortalidadDate})`,
    },
    {
      icon: faUtensils,
      color: "rgb(107 185 69)",
      text: `${totalAlimentoConsumido} kg alimento (${formattedAlimentoDate})`,
    },
  ];

  const resetTicker = () => {
    if (tickerRef.current) {
      tickerRef.current.style.animation = "none"; // Detener la animación
      const reflow = () => tickerRef.current.offsetHeight; // Forzar el reflow
      reflow();
      tickerRef.current.style.animation = ""; // Reiniciar la animación
    }
  };

  return (
    <>
      <div className="percentages flex-space-between align-items-center">
        {/* Flechitas para reiniciar el ticker */}
        <div className="ticker-reset" onClick={resetTicker}>
          <FontAwesomeIcon icon={faAngleDoubleLeft} className="ticker-arrow" />
        </div>

        {/* Mostrar el icon-ticker en un bucle infinito */}
        <div
          className={`icon-ticker-container ${isDarkMode ? "dark" : "light"}`}
          style={{ "--ticker-speed": `${speed}s` }}
        >
          <div className="icon-ticker" ref={tickerRef}>
            {tickerItems.map((item, index) => (
              <div className="icon-item" key={index}>
                <FontAwesomeIcon icon={item.icon} style={{ color: item.color }} />
                <span>{item.text}</span>
              </div>
            ))}
            {/* Repetimos los elementos para el loop infinito */}
            {tickerItems.map((item, index) => (
              <div className="icon-item" key={`duplicate-${index}`}>
                <FontAwesomeIcon icon={item.icon} style={{ color: item.color }} />
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Sección de botones fijos dependiendo de la página */}
      {!isGranjaPage && (
        <div className={isDarkMode ? "fixed-dashboard-buttons" : "fixed-dashboard-buttons light"}>
          {isConfiguracionPage && (
            <>
              <AddFarm />
              <AddGalpon />
              <AddBatch />
              <ReduceAves />
              <button
                className="btn btn-green"
                onClick={() => navigate("/mis-alimentos")}
              >
                Mis Alimentos
              </button>
              <button
                className="btn btn-green"
                onClick={() => navigate("/tipos-de-huevo")}
              >
                Tipos de Huevo
              </button>
            </>
          )}

          {isAlimentosPage && (
            <>
              <AddAlimento />
            </>
          )}

          {isGalponesPage && (
            <>
              <AddGalpon />
            </>
          )}

          {isAvesPage && (
            <>
              <AddBatch />
              <ReduceAves />
            </>
          )}
        </div>
      )}
    </>
  );
};

export default DashboardButtons;
